import { isActionLinkRouteName } from '@shared/actionLinks/pages.js'

export const COOKIES_MARKETING_COOKIE_NAME = 'cookies-marketing'
export const COOKIES_TRACKING_COOKIE_NAME = 'cookies-analytics'
export const WEB_MARKETING_QUERY_PARAM = 'web_marketing'
export const WEB_ANALYTICS_QUERY_PARAM = 'web_analytics'

export const normalizeConsentValue = (value) => {
  if (value === true || value === 'true') {
    return true
  }

  if (value === false || value === 'false') {
    return false
  }

  return null
}

export const getCookieValue = (cookieString = '', cookieName) => {
  if (typeof cookieName !== 'string' || cookieName.length === 0) {
    return null
  }

  const matchingCookie = String(cookieString)
    .split('; ')
    .find((cookieEntry) => cookieEntry.startsWith(`${cookieName}=`))

  if (!matchingCookie) {
    return null
  }

  const rawCookieValue = matchingCookie.split('=').slice(1).join('=')

  try {
    return decodeURIComponent(rawCookieValue)
  } catch {
    return rawCookieValue
  }
}

export const getAnonymousTrackingConsents = ({
  initialQuery,
  cookies,
} = {}) => {
  const marketingFromQuery = normalizeConsentValue(
    initialQuery?.[WEB_MARKETING_QUERY_PARAM],
  )
  const trackingFromQuery = normalizeConsentValue(
    initialQuery?.[WEB_ANALYTICS_QUERY_PARAM],
  )

  return {
    marketing:
      marketingFromQuery ??
      normalizeConsentValue(
        getCookieValue(cookies, COOKIES_MARKETING_COOKIE_NAME),
      ),
    webTracking:
      trackingFromQuery ??
      normalizeConsentValue(
        getCookieValue(cookies, COOKIES_TRACKING_COOKIE_NAME),
      ),
  }
}

export const getSignupConsents = (context = {}) => {
  const { marketing, webTracking } = getAnonymousTrackingConsents(context)

  return {
    acceptedMarketing: marketing,
    acceptedWebTracking: webTracking,
  }
}

export const getAuthenticatedTrackingConsents = (user) => ({
  tracking: normalizeConsentValue(user?.consents?.tracking),
  webTracking: normalizeConsentValue(user?.consents?.web_tracking),
})

// True when EU session replay should be active: web_tracking consent is given
// and the user is not in DiGA territory (stricter consent rules apply there).
export const shouldEnableEuSessionReplay = ({
  user,
  routeName,
  initialQuery,
  cookies,
  digaActivated,
} = {}) => {
  if (isInDiga({ routeName, digaActivated, user })) return false

  if (user == null) {
    const { webTracking } = getAnonymousTrackingConsents({
      initialQuery,
      cookies,
    })
    return webTracking === true
  }

  const { webTracking } = getAuthenticatedTrackingConsents(user)
  return webTracking === true
}

export const isAssessmentFlowRouteName = (routeName) =>
  String(routeName || '').startsWith('assessment-')

// True when the user is in DiGA territory: the session after DiGA activation, an assessment
// flow (unless its part of the "pre subscription flow"), or an action link page, which shows a
// prescription to a user who isn't logged in
export const isInDiga = ({ routeName, digaActivated, user }) =>
  digaActivated === true ||
  isActionLinkRouteName(routeName) ||
  (isAssessmentFlowRouteName(routeName) && !!user?.currentSubscription)

export const shouldEnableTracker = ({
  env,
  isFromApp,
  performanceTrackingEnabled,
  user,
  routeName,
  initialQuery,
  cookies,
  digaActivated,
}) => {
  // US everywhere checks tracking consent or in-app rule.
  if (env.isUs) {
    return Boolean(performanceTrackingEnabled) || isFromApp
  }

  // Anonymous EU users rely on landing query params and consent cookies.
  if (user == null) {
    const { marketing, webTracking } = getAnonymousTrackingConsents({
      initialQuery,
      cookies,
    })

    return marketing === true || webTracking === true
  }

  const { tracking, webTracking } = getAuthenticatedTrackingConsents(user)

  // inside the DiGA requires explicit tracking consent.
  if (isInDiga({ routeName, digaActivated, user })) {
    return tracking === true
  }

  // All other authenticated EU cases follow web_tracking consent.
  return webTracking === true
}
